import { Shield, MessageCircle, MapPin, AlertCircle, HeartCrack, BadgeCheck, Megaphone, Phone, Heart } from 'lucide-react';
import LegalLayout from '../../components/LegalLayout';

function SafetyCard({ icon: Icon, iconColor, iconBg, title, tips }) {
  return (
    <section
      className="bg-white border border-[#F4F1DE] rounded-2xl p-5 sm:p-6"
      data-testid={`safety-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <Icon className="w-6 h-6" style={{ color: iconColor }} />
        </div>
        <h2 className="text-lg font-bold text-[#2B2D42]">{title}</h2>
      </div>
      <ul className="space-y-2">
        {tips.map((tip, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#3A3D52] leading-relaxed">
            <span className="text-[#B5B5B5] mt-0.5 flex-shrink-0">›</span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function Safety() {
  return (
    <LegalLayout
      title="Safety Tips"
      badge="Stay Safe"
      badgeIcon={Shield}
      badgeColor="#5A8DBF"
      lastUpdated="April 5, 2026"
      introBorderColor="#5A8DBF"
      intro="Your safety is our priority. Follow these tips to protect yourself while using AfroConnect and meeting people in real life."
    >
      <SafetyCard
        icon={MessageCircle}
        iconColor="#5A8DBF"
        iconBg="#E3F2FD"
        title="Online Safety"
        tips={[
          'Keep conversations on AfroConnect until you feel comfortable',
          'Never share financial information or send money',
          'Be wary of anyone asking for gift cards, wire transfers, or crypto',
          "Don't share personal info like your address or workplace too early",
          'Trust your instincts — if something feels off, it probably is',
          'Use video chat before meeting to verify identity',
        ]}
      />

      <SafetyCard
        icon={MapPin}
        iconColor="#C8553D"
        iconBg="#FFE5DD"
        title="Meeting In Person"
        tips={[
          'Always meet in a public place for your first date',
          "Tell a friend or family member where you're going",
          'Share your live location with someone you trust',
          "Arrange your own transportation — don't rely on your date",
          'Stay sober and alert on first meetings',
          "Don't feel pressured to go somewhere private",
          'Have an exit plan ready if you feel uncomfortable',
        ]}
      />

      <SafetyCard
        icon={AlertCircle}
        iconColor="#E76F51"
        iconBg="#FFEBEE"
        title="Red Flags to Watch For"
        tips={[
          'They refuse to video chat or meet in person',
          'They ask for money or financial help',
          "Their story doesn't add up or keeps changing",
          'They pressure you to move off the app quickly',
          'They claim to be overseas or in the military (common scam)',
          'They profess love or strong feelings very quickly',
          'They try to isolate you from friends or family',
          'They become aggressive when you set boundaries',
        ]}
      />

      <SafetyCard
        icon={HeartCrack}
        iconColor="#9C27B0"
        iconBg="#F3E5F5"
        title="Romance Scams"
        tips={[
          'Scammers build fake emotional connections to exploit you',
          "Never send money to someone you haven't met",
          'Be skeptical of overseas military or business stories',
          "If they can't video chat, that's a major red flag",
          'Report suspicious profiles immediately',
          "If you've sent money, contact your bank and local police",
        ]}
      />

      <SafetyCard
        icon={BadgeCheck}
        iconColor="#2A9D8F"
        iconBg="#E8F5E9"
        title="Verification Matters"
        tips={[
          'Look for the verified badge on profiles',
          'Get verified yourself to build trust',
          'Verified users have confirmed their identity with a selfie',
          "Verification doesn't guarantee safety — always be cautious",
          'Report fake verification badges',
        ]}
      />

      {/* Report steps */}
      <section className="bg-[#FFF8E1] border border-[#FFE9A8] rounded-2xl p-5 sm:p-6 text-center">
        <Megaphone className="w-10 h-10 text-[#C8553D] mx-auto mb-3" />
        <h2 className="text-xl font-bold text-[#2B2D42] mb-2">Report & Block</h2>
        <p className="text-sm text-[#3A3D52] mb-5 leading-relaxed">
          If someone makes you uncomfortable or violates our guidelines:
        </p>
        <ol className="space-y-3 text-left max-w-md mx-auto mb-4">
          {[
            'Tap the menu icon on their profile',
            'Select "Report" or "Block"',
            'Choose a reason and submit',
          ].map((step, i) => (
            <li key={i} className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-[#C8553D] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                {i + 1}
              </span>
              <span className="text-sm text-[#2B2D42]">{step}</span>
            </li>
          ))}
        </ol>
        <p className="text-xs italic text-[#6D6875]">
          Reports are confidential — the other person won't know.
        </p>
      </section>

      {/* Emergency */}
      <section className="bg-[#E76F51] rounded-2xl p-5 sm:p-6 text-center text-white" data-testid="safety-emergency">
        <Phone className="w-8 h-8 mx-auto mb-3" />
        <h2 className="text-xl font-bold mb-1.5">Emergency Resources</h2>
        <p className="text-sm text-white/90 mb-5">
          If you're in immediate danger, call 911
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href="tel:911"
            data-testid="safety-call-911"
            className="inline-flex items-center gap-2 bg-white text-[#E76F51] font-bold text-sm px-4 py-2.5 rounded-full hover:bg-[#FFF8F5] transition-colors"
          >
            <Phone className="w-4 h-4" />
            Call 911
          </a>
          <a
            href="https://www.canada.ca/en/public-health/services/mental-health-services/mental-health-get-help.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="safety-mental-health"
            className="inline-flex items-center gap-2 bg-white text-[#E76F51] font-bold text-sm px-4 py-2.5 rounded-full hover:bg-[#FFF8F5] transition-colors"
          >
            <Heart className="w-4 h-4" />
            Mental Health Help
          </a>
        </div>
      </section>

      <div className="text-center pt-4 flex items-center justify-center gap-2 text-[#6D6875] text-sm">
        <Shield className="w-4 h-4 text-[#5A8DBF]" />
        <span>Stay safe, stay connected</span>
      </div>
    </LegalLayout>
  );
}
